import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MobileBottomNav } from './MobileBottomNav';
import { FriendsDrawerClient } from '@/components/social/FriendsDrawerClient';
import { FriendsButton } from '@/components/social/FriendsButton';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import type { Profile } from '@/types';

interface ArcadiaAppShellProps {
  children: React.ReactNode;
  sidebar: React.ReactNode;
  topBar: React.ReactNode;
  profile: Profile | null;
}

export function ArcadiaAppShell({ children, sidebar, topBar, profile }: ArcadiaAppShellProps) {
  const [friendsDrawerOpen, setFriendsDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const mainSidebarInset =
    pathname === '/'
      ? 'md:ml-0'
      : pathname === '/store'
        ? 'md:ml-72'
        : pathname.startsWith('/store/')
          ? 'md:ml-0'
          : pathname.startsWith('/developer/portal')
            ? 'md:ml-0'
            : 'md:ml-64';

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <a
        href="#arcadia-main-content"
        className="skip-to-main"
      >
        Skip to main content
      </a>
      {React.cloneElement(topBar as React.ReactElement<{ friendsSlot?: React.ReactNode }>, {
        friendsSlot: !isMobile ? (
          <FriendsButton
            onClick={() => setFriendsDrawerOpen(!friendsDrawerOpen)}
            isOpen={friendsDrawerOpen}
          />
        ) : undefined,
      })}

      <div className="flex min-h-0 flex-1 overflow-hidden pt-16">
        {!isMobile && sidebar}

        <main
          id="arcadia-main-content"
          tabIndex={-1}
          className={cn(
            'min-h-0 flex-1 overflow-y-auto bg-arc-bg outline-none',
            !isMobile && mainSidebarInset,
            isMobile && 'pb-16',
          )}
        >
          {children}
        </main>
      </div>

      {isMobile && (
        <MobileBottomNav onOpenFriends={() => setFriendsDrawerOpen(true)} />
      )}

      <FriendsDrawerClient
        profile={profile}
        isOpen={friendsDrawerOpen}
        onClose={() => setFriendsDrawerOpen(false)}
      />
    </div>
  );
}
